import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Trash2,
  Heart,
  Calendar,
  Search,
  Loader2,
  X,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import { RiskBadge } from '../components/RiskBadge';
import { useProductScanner } from '../hooks/useProductScanner';
import type { ProductScan } from '../types';

type FilterMode = 'all' | 'favorites' | 'safe' | 'caution' | 'avoid';

export function ScanHistoryPage() {
  const { t } = useTranslation('scanner');
  const navigate = useNavigate();
  const { getScanHistory, toggleFavorite, deleteScan, isLoading } = useProductScanner();

  const [scans, setScans] = useState<ProductScan[]>([]);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [deleteConfirmScan, setDeleteConfirmScan] = useState<ProductScan | null>(null);

  // Load scans on mount
  useEffect(() => {
    loadScans();
  }, []);

  const loadScans = async () => {
    const data = await getScanHistory(100);
    setScans(data);
  };

  // Filter and search scans
  const filteredScans = useMemo(() => {
    let result = scans;

    if (filter === 'favorites') {
      result = result.filter((s) => s.is_favorite);
    } else if (filter !== 'all') {
      result = result.filter((s) => s.scan_result === filter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.product?.name?.toLowerCase().includes(query) ||
          s.product?.brand?.toLowerCase().includes(query) ||
          s.barcode.includes(query)
      );
    }

    return result;
  }, [scans, filter, searchQuery]);

  // Group scans by date
  const groupedScans = useMemo(() => {
    const groups: Record<string, ProductScan[]> = {};

    filteredScans.forEach((scan) => {
      const date = new Date(scan.scanned_at).toLocaleDateString('de-DE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(scan);
    });

    return groups;
  }, [filteredScans]);

  // Handle favorite toggle
  const handleToggleFavorite = async (scan: ProductScan) => {
    const success = await toggleFavorite(scan.id, !scan.is_favorite);
    if (success) {
      setScans((prev) =>
        prev.map((s) =>
          s.id === scan.id ? { ...s, is_favorite: !s.is_favorite } : s
        )
      );
    }
  };

  // Handle delete
  const handleDelete = async (scan: ProductScan) => {
    const success = await deleteScan(scan.id);
    if (success) {
      setScans((prev) => prev.filter((s) => s.id !== scan.id));
    }
    setDeleteConfirmScan(null);
  };

  // Filter counts
  const filterCounts = useMemo(() => ({
    all: scans.length,
    favorites: scans.filter((s) => s.is_favorite).length,
    safe: scans.filter((s) => s.scan_result === 'safe').length,
    caution: scans.filter((s) => s.scan_result === 'caution').length,
    avoid: scans.filter((s) => s.scan_result === 'avoid').length,
  }), [scans]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-white text-2xl font-semibold">{t('history.title')}</h1>
          <p className="text-white/60 text-sm">
            {t('history.productCount', { count: scans.length })}
          </p>
        </div>
        <button
          onClick={() => setShowSearch(!showSearch)}
          className="glass-effect p-2.5 rounded-xl text-white"
        >
          {showSearch ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
        </button>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder={t('history.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange text-sm"
              autoFocus
            />
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs mb-6">
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          count={filterCounts.all}
        >
          {t('history.filterAll')}
        </FilterButton>
        <FilterButton
          active={filter === 'favorites'}
          onClick={() => setFilter('favorites')}
          count={filterCounts.favorites}
          icon={<Heart className="w-3 h-3" />}
        >
          {t('history.filterFavorites')}
        </FilterButton>
        <FilterButton
          active={filter === 'safe'}
          onClick={() => setFilter('safe')}
          count={filterCounts.safe}
          color="green"
        >
          {t('history.filterSafe')}
        </FilterButton>
        <FilterButton
          active={filter === 'caution'}
          onClick={() => setFilter('caution')}
          count={filterCounts.caution}
          color="amber"
        >
          {t('history.filterCaution')}
        </FilterButton>
        <FilterButton
          active={filter === 'avoid'}
          onClick={() => setFilter('avoid')}
          count={filterCounts.avoid}
          color="red"
        >
          {t('history.filterAvoid')}
        </FilterButton>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      ) : filteredScans.length === 0 ? (
        <div className="glass-effect rounded-[15px] p-8 text-center border border-dashed border-white/20">
          <Package className="w-12 h-12 text-white/30 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-white mb-2">
            {filter === 'all' && !searchQuery
              ? t('history.empty')
              : t('history.noResults')}
          </h2>
          <p className="text-white/50 text-sm mb-4">
            {filter === 'all' && !searchQuery
              ? t('history.emptyHint')
              : t('history.noResultsHint')}
          </p>
          {filter === 'all' && !searchQuery && (
            <button
              onClick={() => navigate('/tools/scanner')}
              className="bg-pp-orange px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 mx-auto"
            >
              <ShieldCheck className="w-4 h-4" />
              {t('history.checkProduct')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedScans).map(([date, dateScans]) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-white/50 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {date}
              </h3>
              <div className="divide-y divide-white/10">
                {dateScans.map((scan) => (
                  <ScanItem
                    key={scan.id}
                    scan={scan}
                    onClick={() => navigate(`/tools/scanner/product/${scan.barcode}`)}
                    onToggleFavorite={() => handleToggleFavorite(scan)}
                    onDelete={() => setDeleteConfirmScan(scan)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmScan && (
        <>
          <div
            className="fixed inset-0 bg-pp-dark/80 backdrop-blur-sm z-[9999]"
            onClick={() => setDeleteConfirmScan(null)}
          />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 glass-effect rounded-[15px] p-6 z-[9999] max-w-sm mx-auto">
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('history.deleteTitle')}
            </h3>
            <p className="text-white/70 mb-4">
              {deleteConfirmScan.product?.name
                ? t('history.deleteDescriptionNamed', { name: deleteConfirmScan.product.name })
                : t('history.deleteDescription')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmScan(null)}
                className="flex-1 py-2.5 rounded-xl glass-effect border border-white/25 text-white font-medium"
              >
                {t('history.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmScan)}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium"
              >
                {t('history.delete')}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Filter Button Component
interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  count: number;
  color?: 'green' | 'amber' | 'red';
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function FilterButton({
  active,
  onClick,
  count,
  color,
  icon,
  children,
}: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'filter-tab',
        active && (color
          ? {
              'bg-green-500 border-green-500': color === 'green',
              'bg-amber-500 border-amber-500': color === 'amber',
              'bg-red-500 border-red-500': color === 'red',
            }
          : 'active')
      )}
    >
      {icon}
      {children}
      <span className="ml-1">{count}</span>
    </button>
  );
}

// Scan Item Component
interface ScanItemProps {
  scan: ProductScan;
  onClick: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}

function ScanItem({ scan, onClick, onToggleFavorite, onDelete }: ScanItemProps) {
  const { t } = useTranslation('scanner');
  const time = new Date(scan.scanned_at).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="py-3 hover:bg-white/5 transition-all flex items-center gap-3">
      {/* Product Image */}
      <div
        className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
        onClick={onClick}
      >
        {scan.product?.image_small_url ? (
          <img
            src={scan.product.image_small_url}
            alt={scan.product.name}
            className="w-full h-full object-contain"
          />
        ) : (
          <Package className="w-6 h-6 text-white/40" />
        )}
      </div>

      {/* Product Info */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <p className="font-medium text-white truncate">
          {scan.product?.name || t('history.unknownProduct')}
        </p>
        <div className="flex items-center gap-2 text-xs text-white/50">
          <span>{scan.product?.brand || scan.barcode}</span>
          <span>•</span>
          <span>{time}</span>
        </div>
      </div>

      {/* Risk Badge */}
      <RiskBadge risk={scan.scan_result} size="sm" showLabel={false} className="!bg-transparent" />

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
          className={cn(
            'p-2 rounded-full transition-colors',
            scan.is_favorite
              ? 'text-red-400 bg-red-500/20'
              : 'text-white/40 hover:text-red-400 hover:bg-red-500/20'
          )}
        >
          <Heart
            className={cn('w-4 h-4', scan.is_favorite && 'fill-current')}
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-2 rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/20 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
